import { LayerProps } from "react-map-gl";
import { LocationRiskAssessment, PageTabs, PolicyDetails } from "../riskAssessment//hazardIdEnum";
import { verbatimLevelMarker } from "../riskAssessment/hazardUtils";
import { HazardIdEnum } from "../riskAssessment/hazardIdEnum";
// declare at top of your file

import pixels from "image-pixels";

interface legendEntry {
  label: string;
  quantity: string;
  color: string;
  opacity?: string;
}

interface wmsLegend {
  Legend: {
    layerName: string;
    title: string;
    rules: {
      symbolizers: [
        {
          Raster: {
            colormap: {
              entries: legendEntry[];

              type: string;
            };
            opacity: string;
          };
        },
      ];
    }[];
  }[];
}

export const genericRasterLayer = (): LayerProps => {
  const rasterLayer: LayerProps = {
    //id: "wms-layer",  // This id is overwritten when the layer is added to the map
    //source: "wms-source", // This source name is overwritten when the layer is added to the map
    type: "raster",
  };

  return rasterLayer;
};

export const landCoverWrlTileUrl = (layerNames: string) => {
  const tilePath =
    "https://geoserver.vlcc.geoville.com/geoserver/ows" +
    "?SERVICE=WMS" +
    "&REQUEST=GetMap" +
    "&VERSION=1.3.0" +
    "&LAYERS=" +
    layerNames +
    // Note: WMS server supports multiple layers, but we use a single layer at a time, because we want to handle just one legend at a time.
    // For example "&LAYERS=HRL_TCF:DLT_S2023,HRL_CPL:CTY_S2023" would be valid.

    "&STYLES=" + // (leave empty unless you need a named style)
    "&FORMAT=image/png" +
    "&TRANSPARENT=true" +
    "&CRS=EPSG:3857" + // <--important change
    "&BBOX={bbox-epsg-3857}" + // <-- Mapbox token(placeholder)
    "&WIDTH=256&HEIGHT=256" + // match tileSize below (or 512/512)
    "&DPI=96&MAP_RESOLUTION=96&FORMAT_OPTIONS=dpi:96";

  // Legend (PNG) is:
  // https://geoserver.vlcc.geoville.com/geoserver/ows?service=WMS&version=1.3.0&request=GetLegendGraphic&format=image%2Fpng&width=20&height=20&layer=HRL_CPL%3ACTY_S2023
  // Codes (JSON) are:
  // https://geoserver.vlcc.geoville.com/geoserver/ows?service=WMS&version=1.3.0&request=GetLegendGraphic&layer=HRL_CPL:CTY_S2023&format=application/json

  return tilePath;
};

export const worldCoverWrlTileUrl = (): string => {
  const tilePath =
    "https://services.terrascope.be/wms/v2" +
    "?SERVICE=WMS" +
    "&REQUEST=GetMap" +
    "&VERSION=1.1.1" +
    "&allowTemporalUpdates=true" +
    "&contextualWMSLegend=1" +
    "&srs=EPSG:3857" +
    "&dpiMode=7" +
    "&enableTime=true" +
    "&featureCount=10" +
    "&format=image/png" +
    "&layers=WORLDCOVER_2021_MAP" +
    "&styles" +
    "&TRANSPARENT=true" +
    "&tilePixelRatio=0" +
    "&timeDimensionExtent=2021-12-31" +
    "&type=wmst" +
    "&url=https://services.terrascope.be/wms/v2" +
    "&BBOX={bbox-epsg-3857}" + // <-- Mapbox token
    "&WIDTH=256&HEIGHT=256" + // match tileSize below (or 512/512)
    "&DPI=96&MAP_RESOLUTION=96&FORMAT_OPTIONS=dpi:96";

  return tilePath;
};

export const wmsGetLegendGraphicUrlForLandCoverJson = (layerName: string) => {
  // We do not use this function, this is just to demonstrate how  the legend can be fetched from the WMS server
  return `https://geoserver.vlcc.geoville.com/geoserver/ows?service=WMS&version=1.3.0&request=GetLegendGraphic&layer=${layerName}&format=application/json`;
};

const getLandCoverGenericType = async (
  lat: number,
  lng: number,
  layerName: string,
  outsideValue: number,
): Promise<number> => {
  // The server supports CRS:84 (lon, lat)
  const wmsUrl =
    "https://geoserver.vlcc.geoville.com/geoserver/ows" +
    "?SERVICE=WMS" +
    "&REQUEST=GetFeatureInfo" +
    "&VERSION=1.3.0" +
    "&LAYERS=" +
    layerName + // HRL_CPL:CTY_S2023" +
    "&QUERY_LAYERS=" +
    layerName + // HRL_CPL:CTY_S2023" +
    "&STYLES=" + // (leave empty unless you need a named style)
    "&FORMAT=image/png" +
    "&TRANSPARENT=true" +
    "&CRS=CRS:84" + // (lon, lat)
    "&BBOX=" +
    (lng - 0.0001).toString() +
    "," +
    (lat - 0.0001).toString() +
    "," +
    (lng + 0.0001).toString() +
    "," +
    (lat + 0.0001).toString() +
    "&WIDTH=101&HEIGHT=101" + // (width and height of the bbox in pixels)
    "&I=50&J=50" + // (the pixel coordinates of the point to query)
    "&INFO_FORMAT=application/json";
  try {
    const response = await fetch(wmsUrl, { cache: "no-cache" });
    // Note, any files that are potentially updated should not be cached.
    if (response.status !== 200) {
      throw new Error(`Failed to fetch WMS GetFeatureInfo from url ${wmsUrl}: ${response.statusText}`);
    }
    const json = await response.json();
    // console.log("WMS GetFeatureInfo response: ", json);

    if (json?.features?.length === 0) {
      return outsideValue; // Outside the map area
    }

    const landCoverValue = json?.features?.[0]?.properties?.PALETTE_INDEX;
    if (landCoverValue !== undefined) {
      return landCoverValue;
    } else {
      throw new Error(`Failed to get land cover value from WMS GetFeatureInfo response`);
    }
  } catch (err) {
    console.error(err);
  }
  return -1;
};

// lon, lat in degrees  ->  x, y in meters (Web Mercator)
function epsg4326to3857(lon: number, lat: number): [number, number] {
  const R = 6378137; // Earth radius (meters)
  const max = 85.05112878; // Web Mercator latitude limit
  const clampedLat = Math.max(Math.min(lat, max), -max);
  const x = (R * lon * Math.PI) / 180;
  const y = R * Math.log(Math.tan(Math.PI / 4 + (clampedLat * Math.PI) / 180 / 2));
  return [x, y];
}

const getWorldCoverClass = async (lat: number, lng: number): Promise<number> => {
  const locationIn3857 = epsg4326to3857(lng, lat);

  // Box 2x2 meters around the point
  const verySmallBox: string =
    (locationIn3857[0] - 1).toString() +
    "," +
    (locationIn3857[1] - 1).toString() +
    "," +
    (locationIn3857[0] + 1).toString() +
    "," +
    (locationIn3857[1] + 1).toString();

  const sideInPixels = 8; // 8x8 pixels image
  const halfSideInPixels = sideInPixels / 2;

  const tilePath =
    "https://services.terrascope.be/wms/v2" +
    "?SERVICE=WMS" +
    "&REQUEST=GetMap" +
    "&VERSION=1.1.1" +
    "&allowTemporalUpdates=true" +
    "&contextualWMSLegend=1" +
    "&srs=EPSG:3857" +
    "&dpiMode=7" +
    "&enableTime=true" +
    "&featureCount=10" +
    "&format=image/png" +
    "&layers=WORLDCOVER_2021_MAP" +
    "&styles" +
    "&TRANSPARENT=true" +
    "&tilePixelRatio=0" +
    "&timeDimensionExtent=2021-12-31" +
    "&type=wmst" +
    "&url=https://services.terrascope.be/wms/v2" +
    "&BBOX=" +
    verySmallBox +
    "" + // <-- Mapbox token
    "&WIDTH=" +
    sideInPixels +
    "&HEIGHT=" +
    sideInPixels +
    "" +
    "&DPI=96&MAP_RESOLUTION=96&FORMAT_OPTIONS=dpi:96";

  let colorAsHexString: string = "#000000";

  try {
    const { data, width } = await pixels(tilePath);
    // data is 8x8 pixels, RGBA format, so 8*8*4=256 elements

    const x = halfSideInPixels,
      y = halfSideInPixels; // center pixel coords in image
    const numberOfRgbaChannelsPerPixel = 4;
    const idx = (y * width + x) * numberOfRgbaChannelsPerPixel;

    const r = data[idx];
    const g = data[idx + 1];
    const b = data[idx + 2];

    // Assume, r=0x23, g=0x45, b=0x67
    // Convert to hex(1) hex(23) hex(45) hex(67) format and then remove the leading 1.
    // It will result in #234567 format, with leading zeros if needed.
    colorAsHexString = "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    const entries = ESA_WorldCover_2021_v2_json.Legend[0].rules[0].symbolizers[0].Raster.colormap.entries;
    const entry = entries.find((e) => e.color.toLowerCase() === colorAsHexString.toLowerCase());
    if (entry) {
      return parseInt(entry.quantity);
    } else {
      console.error("Failed to match color:", colorAsHexString);
      return -1;
    }
  } catch (err) {
    console.error("Failed to read pixels:", err);
    return -1;
  }
};

const getWorldCoverClassThatDoesNotWork = async (lat: number, lng: number): Promise<number> => {
  // This function does not work, because the WMS server does not support GetFeatureInfo!
  // It is just a demo of how it would be done if the server supported GetFeatureInfo.
  // Most server do not support GetFeatureInfo for WMTS layers.
  // The server even does not support CRS:84/EPSG:4326 (lon, lat)
  const wmsUrl =
    "https://services.terrascope.be/wms/v2" +
    "?SERVICE=WMS" +
    "&REQUEST=GetFeatureInfo" +
    "&VERSION=1.1.1" +
    "&LAYERS=WORLDCOVER_2021_MAP" +
    "&QUERY_LAYERS=WORLDCOVER_2021_MAP" +
    "&STYLES=" + // (leave empty unless you need a named style)
    "&FORMAT=image/png" +
    "&TRANSPARENT=true" +
    "&SRS=EPSG:4326" + // (lat, lon)
    "&BBOX=" +
    (lat - 0.0001).toString() +
    "," +
    (lng - 0.0001).toString() +
    "," +
    (lat + 0.0001).toString() +
    "," +
    (lng + 0.0001).toString() +
    "&WIDTH=101&HEIGHT=101" + // (width and height of the bbox in pixels)
    "&X=50&Y=50" + // (the pixel coordinates of the point to query)
    "&INFO_FORMAT=application/json" +
    "&time=2021-12-31";
  try {
    const response = await fetch(wmsUrl, { cache: "no-cache" });
    // Note, any files that are potentially updated should not be cached.
    if (response.status !== 200) {
      throw new Error(`Failed to fetch WMS GetFeatureInfo from url ${wmsUrl}: ${response.statusText}`);
    }
    const json = await response.json();
    console.log("WMS GetFeatureInfo response: ", json);
    if (json?.features?.length === 0) {
      return 255; // Outside the map area
    }
    const landCoverValue = json?.features?.[0]?.properties?.WORLDCOVER_2021_MAP;
    if (landCoverValue !== undefined) {
      return landCoverValue;
    } else {
      throw new Error(`Failed to get land cover value from WMS GetFeatureInfo response`);
    }
  } catch (err) {
    console.error(err);
  }
  return -1;
};

export const landCoverForestTypeEvaluator = async (lat: number, lng: number) => {
  const landCoverValue = await getLandCoverGenericType(lat, lng, "HRL_TCF:DLT_S2023", 255);
  const landCoverRatingAndLevelAnsMaxRateAccepted = getLandCoverRatingAndLevelAnsMaxRateAccepted(
    landCoverValue,
    CLMS_HRL_ForestTypes_S2023_R10m_json.Legend[0].rules[0].symbolizers[0].Raster.colormap.entries,
  );
  return {
    value: landCoverValue,
    ...landCoverRatingAndLevelAnsMaxRateAccepted,
  };
};

export const landCoverCropTypeEvaluator = async (lat: number, lng: number) => {
  const landCoverValue = await getLandCoverGenericType(lat, lng, "HRL_CPL:CTY_S2023", 65535);
  const landCoverRatingAndLevelAnsMaxRateAccepted = getLandCoverRatingAndLevelAnsMaxRateAccepted(
    landCoverValue,
    CLMS_HRL_CropTypes_S2023_R10m_json.Legend[0].rules[0].symbolizers[0].Raster.colormap.entries,
  );
  return {
    value: landCoverValue,
    ...landCoverRatingAndLevelAnsMaxRateAccepted,
  };
};

export const worldCoverTypeEvaluator = async (lat: number, lng: number) => {
  const landCoverValue = await getWorldCoverClass(lat, lng);
  const landCoverRatingAndLevelAnsMaxRateAccepted = getLandCoverRatingAndLevelAnsMaxRateAccepted(
    landCoverValue,
    ESA_WorldCover_2021_v2_json.Legend[0].rules[0].symbolizers[0].Raster.colormap.entries,
  );
  return {
    value: landCoverValue,
    ...landCoverRatingAndLevelAnsMaxRateAccepted,
  };
};

export function landCoverGenericTypeDescription(variant: string): string {
  return (
    variant +
    " types in Europe in 2023. " +
    " Data from Copernicus Land Monitoring Service (CLMS) High Resolution Layer (HRL)." +
    " Processed by Geoville GmbH and Vito Remote Sensing." +
    " Processed by Cystellar."
  );
}

export function worldCoverTypeDescription(): string {
  return (
    "World cover classes in 2021." +
    " Data from European Space Agency (ESA) based on Copernicus-1 and Copernicus-2 data.  Land Monitoring Service (CLMS) High Resolution Layer (HRL).\n" +
    " Processed by Vito Remote Sensing.\n" +
    " Processed by Cystellar.\n" +
    "© ESA WorldCover project 2021 / Contains modified Copernicus Sentinel data (2021) processed by ESA WorldCover consortium"
  );
}

// https://geoserver.vlcc.geoville.com/geoserver/ows?service=WMS&version=1.3.0&request=GetLegendGraphic&layer=HRL_CPL:CTY_S2023&format=application/json

export const CLMS_HRL_CropTypes_S2023_R10m_json: wmsLegend = {
  Legend: [
    {
      layerName: "CTY_S2023",
      title: "CLMS_HRL_CropTypes_S2023_R10m",
      rules: [
        {
          symbolizers: [
            {
              Raster: {
                colormap: {
                  entries: [
                    {
                      label: "No Cropland",
                      quantity: "0",
                      color: "#F0F0F0",
                      opacity: "0.0",
                    },
                    {
                      label: "Wheat",
                      quantity: "1110",
                      color: "#EE6E32",
                    },
                    {
                      label: "Barley",
                      quantity: "1120",
                      color: "#FBA24A",
                    },
                    {
                      label: "Maize",
                      quantity: "1130",
                      color: "#FADC14",
                    },
                    {
                      label: "Rice",
                      quantity: "1140",
                      color: "#E94301",
                    },
                    {
                      label: "Other Cereals",
                      quantity: "1150",
                      color: "#E8A995",
                    },
                    {
                      label: "Fresh Vegetables",
                      quantity: "1210",
                      color: "#AEC7E8",
                    },
                    {
                      label: "Dry Pulses",
                      quantity: "1220",
                      color: "#4897BF",
                    },
                    {
                      label: "Potatoes",
                      quantity: "1310",
                      color: "#C98C43",
                    },
                    {
                      label: "Sugar Beet",
                      quantity: "1320",
                      color: "#9C5B0C",
                    },
                    {
                      label: "Sunflower",
                      quantity: "1410",
                      color: "#FF7979",
                    },
                    {
                      label: "Soybeans",
                      quantity: "1420",
                      color: "#A86A96",
                    },
                    {
                      label: "Rapeseed",
                      quantity: "1430",
                      color: "#E377C2",
                    },
                    {
                      label: "Flax cotton and hemp",
                      quantity: "1440",
                      color: "#F7B6D2",
                    },
                    {
                      label: "Grapes",
                      quantity: "2100",
                      color: "#DBDB8D",
                    },
                    {
                      label: "Olives",
                      quantity: "2200",
                      color: "#C1CE12",
                    },
                    {
                      label: "Fruits",
                      quantity: "2310",
                      color: "#79A03A",
                    },
                    {
                      label: "Nuts",
                      quantity: "2320",
                      color: "#5A7C30",
                    },
                    {
                      label: "Unclassified arable crop",
                      quantity: "3100",
                      color: "#D7D7D7",
                    },
                    {
                      label: "Unclassified permanent crop",
                      quantity: "3200",
                      color: "#ABABAB",
                    },
                    {
                      label: "Outside area",
                      quantity: "65535",
                      color: "#000000",
                      opacity: "0.0",
                    },
                  ],
                  type: "values",
                },
                opacity: "1.0",
              },
            },
          ],
        },
      ],
    },
  ],
};

// https://geoserver.vlcc.geoville.com/geoserver/ows?service=WMS&version=1.3.0&request=GetLegendGraphic&layer=HRL_TCF:DLT_S2023&format=application/json

export const CLMS_HRL_ForestTypes_S2023_R10m_json: wmsLegend = {
  Legend: [
    {
      layerName: "DLT_S2023",
      title: "CLMS_HRL_DominantLeafType_S2023_R10m",
      rules: [
        {
          symbolizers: [
            {
              Raster: {
                colormap: {
                  entries: [
                    {
                      label: "Non-tree covered areas",
                      quantity: "0",
                      color: "#F0F0F0",
                      opacity: "0.0",
                    },
                    {
                      label: "Broadleaved trees",
                      quantity: "1",
                      color: "#469E4A",
                    },
                    {
                      label: "Coniferous trees",
                      quantity: "2",
                      color: "#1C5C24",
                    },
                    {
                      label: "Outside area",
                      quantity: "255",
                      color: "#000000",
                      opacity: "0.0",
                    },
                  ],
                  type: "values",
                },
                opacity: "1.0",
              },
            },
          ],
        },
      ],
    },
  ],
};

export const ESA_WorldCover_2021_v2_json: wmsLegend = {
  // This comes from https://esa-worldcover.s3.eu-central-1.amazonaws.com/v200/2021/docs/WorldCover_PUM_V2.0.pdf
  Legend: [
    {
      layerName: "WORLDCOVER_2021_V2",
      title: "ESA WorldCover 2021 v2 (10m)",
      rules: [
        {
          symbolizers: [
            {
              Raster: {
                colormap: {
                  entries: [
                    {
                      label: "Tree cover",
                      quantity: "10",
                      color: "#006400",
                    },
                    {
                      label: "Shrubland",
                      quantity: "20",
                      color: "#FFBB22",
                    },
                    {
                      label: "Grassland",
                      quantity: "30",
                      color: "#FFFF4C",
                    },
                    {
                      label: "Cropland",
                      quantity: "40",
                      color: "#F096FF",
                    },
                    {
                      label: "Built-up",
                      quantity: "50",
                      color: "#FA0000",
                    },
                    {
                      label: "Bare / sparse vegetation",
                      quantity: "60",
                      color: "#B4B4B4",
                    },
                    {
                      label: "Snow and ice",
                      quantity: "70",
                      color: "#F0F0F0",
                    },
                    {
                      label: "Permanent water bodies",
                      quantity: "80",
                      color: "#0064c8",
                    },
                    {
                      label: "Herbaceous wetland",
                      quantity: "90",
                      color: "#0096a0",
                    },
                    {
                      label: "Mangroves",
                      quantity: "95",
                      color: "#00cf75",
                    },
                    {
                      label: "Moss and lichen",
                      quantity: "100",
                      color: "#fae6a0",
                    },
                    {
                      label: "Outside area",
                      quantity: "255",
                      color: "#000000",
                    },
                  ],
                  type: "values",
                },
                opacity: "1.0",
              },
            },
          ],
        },
      ],
    },
  ],
};

export function CLMS_HRL_CropTypes_S2023_R10m_json_color_list() {
  return CLMS_HRL_CropTypes_S2023_R10m_json.Legend[0].rules[0].symbolizers[0].Raster.colormap.entries.map(
    (e) => e.color,
  );
}

export function CLMS_HRL_ForestTypes_S2023_R10m_json_color_list() {
  return CLMS_HRL_ForestTypes_S2023_R10m_json.Legend[0].rules[0].symbolizers[0].Raster.colormap.entries.map(
    (e) => e.color,
  );
}

export function WorldCover2021_json_color_list() {
  return ESA_WorldCover_2021_v2_json.Legend[0].rules[0].symbolizers[0].Raster.colormap.entries.map(
    (e) => e.color,
  );
}

export function getLandCoverRatingAndLevelAnsMaxRateAccepted(
  landCoverValue: number,
  entries: legendEntry[],
): {
  rating: number;
  level: string;
  maxRateAccepted: number;
} {
  const entryIndex = entries.findIndex((e) => e.quantity === landCoverValue.toString());
  if (entryIndex === -1) {
    console.error(`Land cover value ${landCoverValue} not found in legend`);
    return { rating: 0, level: "Unclassified", maxRateAccepted: entries.length };
  }
  return {
    rating: entryIndex,
    level: `${verbatimLevelMarker}${entries[entryIndex].label}`,
    // We do not translate verbatim level names, for example exotic crop names, like "Potatoes"
    maxRateAccepted: entries.length,
  };
}
